import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Field from '../../../common/components/field';
import FarmSelect from '../../../common/components/farm-select';
import { IconClose } from '../../../common/icons';
import { useClientFarms } from '../../../common/contexts/farms';
import { envelopeData } from '../../../core/network/envelope';
import { RECIPIENT_NAME_MAX, RECIPIENT_NUMBER_MAX } from '../../../core/constants/forms';
import { recipientKeys } from '../queries/keys';
import { useCreateRecipient, useUpdateRecipient } from '../contexts/recipient-mutations';

import '../styles/recipient-form.css';

// TSS vite/03 — alta/edición de una receptora del cliente (public-recipients.md,
// Q-121). Modal role="dialog". Field (number*, name) + FarmSelect (farm_id*).
// Validación local → mutación → err.fields por campo + toast. NUNCA envía
// client_id/animal_id (el backend los fuerza/ignora).
//
// Mapeo de errores del contrato: 422 { fields:{farm_id} } → bajo el FarmSelect;
// 409 (colisión farm_id+number) → bajo `number`; 404 → toast + refetch + cerrar;
// 400 { fields } → por campo.

export default function RecipientForm({ recipient, onClose, onSaved }) {
    const editing = !!recipient;
    const qc = useQueryClient();
    const create = useCreateRecipient();
    const update = useUpdateRecipient(recipient?.id);
    const busy = create.isPending || update.isPending;

    // Deduped con el FarmSelect (misma queryKey): sirve para bloquear el submit
    // cuando el cliente no tiene fincas.
    const { data: farmsData } = useClientFarms({ limit: 500, offset: 0 });
    const hasFarms = (envelopeData(farmsData).items ?? []).length > 0;

    const [number, setNumber] = useState(recipient?.number ?? '');
    const [name, setName] = useState(recipient?.name ?? '');
    const [farmId, setFarmId] = useState(recipient?.farm?.id ?? null);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    const trimmedNumber = number.trim();

    const localError = useMemo(() => {
        if (!trimmedNumber) return { number: 'El número es obligatorio.' };
        if (trimmedNumber.length > RECIPIENT_NUMBER_MAX) return { number: `Máximo ${RECIPIENT_NUMBER_MAX} caracteres.` };
        if (name.trim().length > RECIPIENT_NAME_MAX) return { name: `Máximo ${RECIPIENT_NAME_MAX} caracteres.` };
        if (!farmId) return { farm_id: 'Selecciona una finca.' };
        return null;
    }, [trimmedNumber, name, farmId]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (localError || busy || !hasFarms) return;

        // Subconjunto documentado. name vacío → null (contract admite null/"").
        const payload = {
            number: trimmedNumber,
            name: name.trim() || null,
            farm_id: farmId,
        };

        setFieldErrors({});
        const mutation = editing ? update : create;
        mutation.mutate(payload, {
            onSuccess: () => {
                toast.success(editing ? 'Receptora actualizada' : 'Receptora creada');
                onSaved?.();
            },
            onError: (err) => {
                if (err?.status === 404) {
                    toast.error('Receptora no encontrada');
                    qc.invalidateQueries({ queryKey: recipientKeys.all });
                    onClose?.();
                    return;
                }
                const f = (err?.fields && typeof err.fields === 'object') ? { ...err.fields } : {};
                if (err?.status === 409 && !f.number) {
                    f.number = 'Ya existe una receptora con ese número en esa finca.';
                }
                if (err?.status === 422 && !f.farm_id) {
                    f.farm_id = 'Esa finca no es válida. Elige una de tus fincas.';
                }
                setFieldErrors(f);
                if (err?.status !== 403 && Object.keys(f).length === 0) {
                    toast.error(err?.message || 'No se pudo guardar la receptora');
                }
            },
        });
    };

    const numberError = localError?.number || fieldErrors.number;
    const nameError = localError?.name || fieldErrors.name;
    const farmError = (!hasFarms ? null : localError?.farm_id) || fieldErrors.farm_id;

    return (
        <div
            className="recipient-form"
            role="dialog"
            aria-modal="true"
            aria-label={editing ? 'Editar receptora' : 'Nueva receptora'}
        >
            <div className="recipient-form__overlay" onClick={busy ? undefined : onClose} aria-hidden="true" />
            <form className="recipient-form__card" onSubmit={handleSubmit} noValidate>
                <header className="recipient-form__head">
                    <h2 className="recipient-form__title">{editing ? 'Editar receptora' : 'Nueva receptora'}</h2>
                    <button
                        type="button"
                        className="recipient-form__close"
                        onClick={onClose}
                        disabled={busy}
                        aria-label="Cerrar"
                    >
                        <IconClose />
                    </button>
                </header>

                <div className="recipient-form__body">
                    <Field
                        label="Número"
                        name="number"
                        value={number}
                        onChange={(e) => setNumber(e.target.value)}
                        error={numberError}
                        required
                        maxLength={RECIPIENT_NUMBER_MAX}
                        disabled={busy}
                        placeholder="R-001"
                    />
                    <Field
                        label="Nombre"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={nameError}
                        maxLength={RECIPIENT_NAME_MAX}
                        disabled={busy}
                        placeholder="Opcional"
                    />
                    <Field label="Finca" name="farm_id" required error={farmError}>
                        <FarmSelect
                            value={farmId}
                            onChange={setFarmId}
                            error={!!farmError}
                            disabled={busy}
                        />
                    </Field>
                </div>

                <footer className="recipient-form__foot">
                    <button
                        type="button"
                        className="recipient-form__btn recipient-form__btn--ghost"
                        onClick={onClose}
                        disabled={busy}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="recipient-form__btn recipient-form__btn--primary"
                        disabled={busy || !!localError || !hasFarms}
                    >
                        {busy ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear receptora'}
                    </button>
                </footer>
            </form>
        </div>
    );
}
